// RunDetail — post-mortem ported pixel-true from prototype RunDetailPage.
// Hero shows pack icon + status chip + telemetry. If failed, an error
// callout. Below: GroupedLayers showing which layers were built (Data
// / Insight / Activation), and an execution timeline with phase steps.

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MissionControlHero } from "@/components/hero/MissionControlHero";
import { TelemetryRow } from "@/components/hero/TelemetryRow";
import { GroupedLayers } from "@/components/grouped/GroupedLayers";
import { api } from "@/lib/api";
import { useConfigStore } from "@/lib/store/configStore";
import { useUiStore } from "@/lib/store/uiStore";
import { PACKS, PHASES, PIPELINE_STEPS, VOLUMES } from "@/lib/data";
import type { Layer, PipelineStep, Profile } from "@/lib/types";

interface RawJob {
  status?: string;
  current_step?: string | null;
  steps_log?: { step?: string; status?: string; pct?: number; log?: string }[];
  error_message?: string | null;
  config?: Record<string, unknown>;
}

export function RunDetail(): JSX.Element {
  const { runId = "" } = useParams();
  const navigate = useNavigate();
  const pickMode = useConfigStore((s) => s.pickMode);
  const showToast = useUiStore((s) => s.showToast);
  const [logOpen, setLogOpen] = useState(false);
  const { data: run } = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api.getRun(runId),
  });
  // The mapped Run shape drops the worker-side log; pull the raw job
  // for current_step / steps_log / error_message so failed runs can
  // surface the actual stack and the timeline can show the real failed
  // phase instead of the hardcoded "Phase 4 · Sales Cloud" string.
  const { data: rawJob } = useQuery<RawJob | null>({
    queryKey: ["run-raw", runId],
    queryFn: async () => {
      const r = await fetch(`/api/jobs/${runId}`, { credentials: "include" });
      if (!r.ok) return null;
      return r.json() as Promise<RawJob>;
    },
  });

  // Resolve packs from the live API (built-in + visible community +
  // own) when we have a confident match — used ONLY for the hero icon
  // accent. Title + subtitle now read straight from the run record
  // instead of falling back to a pack name, so an unmatched profileId
  // never silently rebrands the run as "Cirrus Renewable Energy"
  // (PACKS[0]'s shape) on the post-mortem page.
  const { data: packs = PACKS } = useQuery<Profile[]>({
    queryKey: ["packs", "for-run-detail"],
    queryFn: api.listPacks,
    staleTime: 60_000,
  });
  const matchedPack = useMemo<Profile | undefined>(
    () =>
      run?.profileId ? packs.find((p) => p.id === run.profileId) : undefined,
    [packs, run?.profileId],
  );
  const vol = useMemo(
    () =>
      VOLUMES.find((v) => v.id === (run?.volume ?? "").toLowerCase()) ??
      VOLUMES[2],
    [run?.volume],
  );
  // Resolve the failed phase from the worker's actual current_step
  // when we have it. Falls back to the last phase index for completed
  // runs and a sentinel for failed runs without a step.
  //
  // CRITICAL: This hook must live above the `if (!run)` early return.
  // Hooks must run in identical order on every render — when `run`
  // transitions from undefined → defined the hook count cannot change,
  // or React throws minified error #310.
  const failedStepName = rawJob?.current_step ?? null;
  const failedPhaseIdx = useMemo(() => {
    if (run?.status !== "failed") return PHASES.length;
    if (!failedStepName) return PHASES.length - 1;
    const i = PHASES.findIndex((p) =>
      failedStepName.toLowerCase().includes(p.label.toLowerCase().split(" ")[0]),
    );
    return i >= 0 ? i : PHASES.length - 1;
  }, [run?.status, failedStepName]);

  if (!run) {
    return (
      <div
        style={{
          maxWidth: 1080,
          margin: "0 auto",
          padding: "60px 24px",
          textAlign: "center",
          color: "var(--ink-3)",
        }}
      >
        Loading run…
      </div>
    );
  }

  const isFailed = run.status === "failed";
  const isRunning = run.status === "running";
  // PROD-A3 (2026-04-21): jobs whose data layer landed but whose
  // analytics layer silently failed surface as ``complete_with_errors``
  // — same affordances as ``complete`` (open in org / refresh / layer)
  // but the chip is amber and the deploy-errors panel below renders.
  const isPartial = run.status === "complete_with_errors";
  const isCompleteLike = run.status === "complete" || isPartial;
  const isTdsxRun = run.layers.includes("tdsx");
  const failedPhaseLabel = PHASES[failedPhaseIdx]?.label ?? "—";
  const errorMessage =
    rawJob?.error_message?.trim() || run.errorMsg || "Worker exited without an error message.";

  const continueLayer = (): void => {
    pickMode("continue", run.id);
    navigate("/configure");
  };
  const refresh = (): void => {
    pickMode("refresh", run.id);
    navigate("/configure");
  };
  const retryFromFailure = (): void => {
    pickMode("continue", run.id);
    navigate("/configure");
  };
  const copyError = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(errorMessage);
      showToast("Error copied to clipboard");
    } catch {
      showToast("Could not copy — clipboard blocked");
    }
  };
  const openSupport = (): void => {
    const subject = encodeURIComponent(
      `Q Branch run failure · ${run.profile ?? run.id}`,
    );
    const body = encodeURIComponent(
      `Run ID: ${run.id}\nProfile: ${run.profile ?? "Demo"}\nFailed phase: ${failedPhaseLabel}\n\nError:\n${errorMessage}\n`,
    );
    window.open(
      `mailto:qbranch-support@salesforce.com?subject=${subject}&body=${body}`,
      "_blank",
    );
  };

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb={
          <button
            onClick={() => navigate("/runs")}
            style={{
              background: "transparent",
              border: 0,
              color: "#AACBE2",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".1em",
              textTransform: "uppercase",
              padding: 0,
              cursor: "pointer",
            }}
          >
            ← Runs
          </button>
        }
        iconSrc={matchedPack?.logo ?? "/assets/tableau-logo.png"}
        title={run.profile ?? "Demo run"}
        subtitle={`${run.volume} · ${run.cloud} · ${run.org}`}
        chips={[
          {
            label: run.startedAt ?? run.completed ?? "—",
            bg: "rgba(255,255,255,.12)",
          },
          isFailed
            ? {
                label: "Failed",
                dot: "var(--pink)",
                bg: "rgba(186,5,23,.25)",
                color: "#ffb3b9",
              }
            : isRunning
              ? {
                  label: "Running",
                  dot: "#4BC076",
                  bg: "rgba(75,192,118,.25)",
                  color: "#A8E5BD",
                  pulse: true,
                }
              : isPartial
                ? {
                    label: "⚠ Completed with errors",
                    dot: "var(--orange)",
                    bg: "rgba(243,154,55,.25)",
                    color: "#ffd9b0",
                  }
                : {
                    label: "✓ Complete",
                    bg: "rgba(75,192,118,.25)",
                    color: "#A8E5BD",
                  },
        ]}
        rightSlot={
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {isCompleteLike && (
              <>
                {isTdsxRun && (
                  <a
                    className="btn ghost"
                    href={`/api/jobs/${run.id}/download-hyper`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      background: "rgba(255,255,255,.12)",
                      color: "white",
                      border: "1px solid rgba(255,255,255,.25)",
                      whiteSpace: "nowrap",
                      textDecoration: "none",
                    }}
                  >
                    ↓ Download .tdsx
                  </a>
                )}
                {run.layers.includes("dc") && (
                  <button
                    className="btn ghost"
                    style={{
                      background: "rgba(255,255,255,.12)",
                      color: "white",
                      border: "1px solid rgba(255,255,255,.25)",
                      whiteSpace: "nowrap",
                    }}
                    onClick={refresh}
                  >
                    ↻ Refresh
                  </button>
                )}
                {/* Partial-success: primary CTA routes to the Monitor's
                    DeployErrorsPanel so the SE can triage what actually
                    failed before they're tempted to "+ Layer" on top
                    of an analytics layer that didn't deploy. */}
                {isPartial ? (
                  <button
                    className="btn primary lg"
                    style={{
                      background: "var(--orange)",
                      color: "white",
                      border: "none",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => navigate(`/monitor/${run.id}`)}
                  >
                    ⚠ View deployment errors
                  </button>
                ) : (
                  <button
                    className="btn primary lg"
                    style={{
                      background: "#fff",
                      color: "var(--eb-15)",
                      border: "none",
                      whiteSpace: "nowrap",
                    }}
                    onClick={continueLayer}
                  >
                    {isTdsxRun ? "Build a full org from this" : "+ Layer onto this"}
                  </button>
                )}
              </>
            )}
            {isFailed && (
              <button
                className="btn primary lg"
                style={{
                  background: "#fff",
                  color: "var(--eb-15)",
                  border: "none",
                  whiteSpace: "nowrap",
                }}
                onClick={retryFromFailure}
              >
                ↻ Retry from failure
              </button>
            )}
          </div>
        }
      >
        <TelemetryRow
          cells={[
            { k: "Accounts",      v: vol?.accounts.toLocaleString() ?? "—" },
            { k: "Cases",         v: vol?.cases.toLocaleString() ?? "—" },
            { k: "Opportunities", v: vol?.opps.toLocaleString() ?? "—" },
            { k: "Stories built", v: run.storiesCount },
            { k: "Duration",      v: run.duration ?? "—" },
          ]}
        />
      </MissionControlHero>

      {isFailed && (
        <div
          className="card"
          style={{
            marginTop: 16,
            padding: 16,
            borderColor: "#f1c4c8",
            background: "#fdf3f4",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "#fbe1e3",
                color: "var(--pink)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              !
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--pink)",
                }}
              >
                Run failed at {failedPhaseLabel}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--ink-2)",
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                {errorMessage}
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                <button
                  className="btn ghost sm"
                  onClick={() => setLogOpen((o) => !o)}
                >
                  {logOpen ? "Hide log" : "View full log"}
                </button>
                <button className="btn ghost sm" onClick={() => void copyError()}>
                  Copy error
                </button>
                <button className="btn ghost sm" onClick={openSupport}>
                  Email support
                </button>
              </div>
              {logOpen && (
                <pre
                  style={{
                    marginTop: 10,
                    padding: 10,
                    background: "#1f2937",
                    color: "#d1d5db",
                    borderRadius: 8,
                    fontSize: 11,
                    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                    maxHeight: 280,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {(rawJob?.steps_log ?? [])
                    .map(
                      (s) =>
                        `[${s.status ?? "?"}] ${s.step ?? "step"} ${
                          s.pct != null ? `(${s.pct}%)` : ""
                        }${s.log ? `\n  ${s.log}` : ""}`,
                    )
                    .join("\n") || "No step log available."}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <div className="label" style={{ marginBottom: 8 }}>
          Layers built
        </div>
        <GroupedLayers
          groups={[
            {
              id: "data",
              layer: "data",
              layerLabel: "Layer 1",
              title: "Data",
              blurb: "Raw records in Salesforce + DC",
              items: PIPELINE_STEPS.filter((s) => s.layer === "data").map(
                (item) => ({ id: item.id, data: item }),
              ),
            },
            {
              id: "insight",
              layer: "insight",
              layerLabel: "Layer 2",
              title: "Insight",
              blurb: "Semantic model on top",
              items: PIPELINE_STEPS.filter((s) => s.layer === "insight").map(
                (item) => ({ id: item.id, data: item }),
              ),
            },
            {
              id: "activation",
              layer: "activation",
              layerLabel: "Layer 3",
              title: "Activation",
              blurb: "Apps + dashboards",
              items: PIPELINE_STEPS.filter(
                (s) => s.layer === "activation",
              ).map((item) => ({ id: item.id, data: item })),
            },
          ]}
          renderItem={(item, acc) => {
            const step = item.data as PipelineStep;
            const built = run.layers.includes(step.id as Layer);
            return (
              <div
                style={{
                  padding: 10,
                  borderRadius: 8,
                  border: `1px solid ${built ? acc.fg : "var(--bd)"}`,
                  background: built ? "white" : "rgba(255,255,255,.5)",
                  opacity: built ? 1 : 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 4,
                    flexShrink: 0,
                    background: built ? acc.fg : "white",
                    border: `1.5px solid ${built ? acc.fg : "var(--bd)"}`,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: 10,
                  }}
                >
                  {built ? "✓" : ""}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--ink)",
                    }}
                  >
                    {step.label}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: "var(--ink-2)",
                      marginTop: 1,
                    }}
                  >
                    {built ? step.desc : "Not built in this run"}
                  </div>
                </div>
              </div>
            );
          }}
        />
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0 }}>
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid var(--bd-2)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ink)",
            }}
          >
            Execution timeline
          </div>
          <button className="btn ghost sm">Download full log ↓</button>
        </div>
        <div
          style={{
            padding: 16,
            fontFamily: "ui-monospace, Menlo, monospace",
            fontSize: 11,
            color: "var(--ink-2)",
            lineHeight: 1.8,
            maxHeight: 280,
            overflow: "auto",
          }}
        >
          {PHASES.map((ph, i) => {
            const phaseFailed = isFailed && i === failedPhaseIdx;
            const phaseDone = i < failedPhaseIdx;
            return (
              <div key={ph.id}>
                <div
                  style={{
                    color: phaseFailed
                      ? "var(--pink)"
                      : phaseDone
                        ? "var(--teal)"
                        : "var(--ink-3)",
                  }}
                >
                  {phaseFailed ? "✗" : phaseDone ? "✓" : "○"} {ph.label}
                </div>
                {phaseDone &&
                  ph.steps.map((s, j) => (
                    <div
                      key={j}
                      style={{ color: "var(--ink-3)", paddingLeft: 14 }}
                    >
                      {"  "}✓ {s}
                    </div>
                  ))}
                {phaseFailed && (
                  <div
                    style={{ color: "var(--pink)", paddingLeft: 14 }}
                  >
                    {"  "}✗ Aborted: {run.errorMsg}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
